import type { Good } from "@/entities/good";
import type { GoodVersion, TimeRange } from "@/entities/goodVersion";

/** API `/goods` stores `category` as a string; empty `{}` breaks validation. */
export function categoryStringForApi(category: Good["category"] | null | undefined): string | undefined {
  if (category == null) return undefined;
  if (typeof category === "string") {
    const t = category.trim();
    return t || undefined;
  }
  const fromWb = category.wb?.name?.trim();
  if (fromWb) return fromWb;
  return undefined;
}

export function wizardCategoryForGood(generalCategory: string | null | undefined): Good["category"] {
  const label = String(generalCategory ?? "").trim();
  if (!label) return {};
  return { wb: { name: label } };
}

export function normalizeActivePeriodEnd(end: string): string {
  const trimmed = String(end ?? "").trim();
  if (!trimmed || trimmed === "now") {
    return new Date().toISOString();
  }
  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString();
  }
  return new Date().toISOString();
}

export function sanitizeActivePeriods(periods: TimeRange[] | undefined | null): TimeRange[] {
  if (!Array.isArray(periods)) return [];
  return periods
    .filter((row) => row && typeof row.start === "string")
    .map((row) => {
      const startParsed = Date.parse(row.start);
      return {
        start: Number.isNaN(startParsed) ? new Date().toISOString() : new Date(startParsed).toISOString(),
        end: normalizeActivePeriodEnd(String(row.end ?? "")),
      };
    })
    .slice(0, 32);
}

export function sanitizeCharacteristics(
  characteristics: Array<{ name: string; value: string }> | undefined | null,
): Array<{ name: string; value: string }> {
  if (!Array.isArray(characteristics)) return [];
  return characteristics
    .map((row) => ({
      name: String(row?.name ?? "").trim(),
      value: String(row?.value ?? "").trim(),
    }))
    .filter((row) => row.name.length > 0 && row.value.length > 0)
    .slice(0, 100);
}

function normalizeVersionContentType(
  contentType: GoodVersion["contentType"] | undefined,
): GoodVersion["contentType"] | undefined {
  if (contentType === "photo") return "images";
  if (contentType === "card" || contentType === "images" || contentType === "video") return contentType;
  return undefined;
}

/** Prepare version payload before POST/PATCH to `/goods/*` (avoids `end: "now"`, empty media, etc.). */
export function sanitizeGoodVersionForApi(version: GoodVersion): GoodVersion {
  const images = Array.isArray(version.media?.images)
    ? version.media.images
        .map((img) => String(img ?? "").trim())
        .filter((url) => url.length > 0 && !url.startsWith("blob:") && !url.startsWith("data:"))
    : [];

  const videoRaw = version.media?.video;
  const video =
    typeof videoRaw === "string" && videoRaw.trim() && videoRaw.trim() !== "null" ? videoRaw.trim() : undefined;

  return {
    ...version,
    title: String(version.title ?? "").trim() || "Товар",
    description: String(version.description ?? "").trim(),
    contentType: normalizeVersionContentType(version.contentType),
    activePeriods: sanitizeActivePeriods(version.activePeriods),
    media: {
      images,
      ...(video ? { video } : {}),
    },
  };
}

export function truncateApiMessage(message: string, maxLen = 300): string {
  if (message.length <= maxLen) return message;
  return `${message.slice(0, maxLen)}…`;
}
